/**
 * Founder-only Distribution Pipeline dashboard (/admin/distribution_pipeline).
 *
 * Surface-agnostic: one funnel per public landing page (coach, real_yield, …),
 * each broken down by ad source/campaign. Ad-agnostic by construction — it shows
 * whatever surfaces/campaigns the data contains, so new ads or pages need no
 * change here. Reads GET /api/admin/distribution/metrics (internal-key gated).
 *
 * Stages: reached → engaged (clicked CTA / asked) → email leads.
 * Note: completed trial signups are not attributed yet — "engaged" is the
 * trial-CTA click, the lead is the email fallback (wtp_intents) / coach unlock.
 */

import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../common/Layout';
import { apiService, DistributionMetrics, DistributionSurface } from '../../services/api';

const C = {
  bg: '#0e1116',
  card: '#161b22',
  sub: '#1c2230',
  border: '#2a3140',
  text: '#e6edf3',
  dim: '#9aa7b8',
  muted: '#6b7688',
  accent: '#5b8cff',
  green: '#3fb950',
  pill: '#222b3a',
};

const WINDOWS = [7, 14, 30, 90];

const fmt = (n: number | null | undefined): string =>
  n === null || n === undefined ? '0' : n.toLocaleString();
const pct = (v: number | null): string =>
  v === null || v === undefined ? '—' : `${v.toFixed(0)}%`;

/** Pretty surface label: 'real_yield' → 'Real-Yield', 'coach' → 'Coach'. */
const surfaceLabel = (s: string): string =>
  s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('-');

const DistributionPipeline: React.FC = () => {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<DistributionMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiService.getDistributionMetrics(days));
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setError('Access denied — REACT_APP_INTERNAL_KEY is missing or wrong for this build.');
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load distribution metrics.');
      }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Layout>
      <div style={{ background: C.bg, minHeight: '100vh', color: C.text, padding: '24px 20px', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>Distribution Pipeline</h1>
              <p style={{ margin: '4px 0 0', color: C.dim, fontSize: 13 }}>
                Every landing page · reached → engaged → email lead · by ad source/campaign
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {WINDOWS.map((w) => (
                <button
                  key={w}
                  onClick={() => setDays(w)}
                  style={{
                    background: days === w ? C.accent : C.sub,
                    color: days === w ? '#fff' : C.dim,
                    border: `1px solid ${days === w ? C.accent : C.border}`,
                    borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                  }}
                >
                  {w}d
                </button>
              ))}
              <button onClick={load} style={{ background: C.sub, color: C.dim, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
                ↻
              </button>
            </div>
          </div>

          {loading && <p style={{ color: C.dim }}>Loading…</p>}
          {error && (
            <div style={{ background: '#2a1416', border: `1px solid #f85149`, borderRadius: 8, padding: '12px 16px', color: '#ffb3ad', fontSize: 13, margin: '12px 0' }}>
              {error}
            </div>
          )}

          {data && !loading && (
            <>
              {/* Cross-surface totals */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 16 }}>
                <Stat label="Reached" value={fmt(data.totals.reached)} sub="all surfaces" />
                <Stat label="Engaged" value={fmt(data.totals.engaged)} sub="clicked CTA / asked" />
                <Stat label="Email leads" value={fmt(data.totals.leads)} sub="unlock + WTP" valueColor={C.green} />
              </div>

              {data.surfaces.length === 0 && (
                <p style={{ color: C.muted, fontSize: 13, marginTop: 18 }}>
                  No funnel events in this window yet.
                </p>
              )}

              {data.surfaces.map((s) => (
                <SurfaceCard key={s.surface} s={s} />
              ))}

              <p style={{ color: C.muted, fontSize: 11, marginTop: 16, lineHeight: 1.6 }}>
                Reach &amp; engagement are server-side from public_coach_events (per surface, by UTM);
                email leads combine /coach unlocks + /real-yield wtp_intents. Completed trial signups
                aren't attributed yet — "engaged" is the trial-CTA click. Raw ad clicks &amp; spend live
                in Reddit Ads Manager. Window: {data.window_days}d.
              </p>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

const SurfaceCard: React.FC<{ s: DistributionSurface }> = ({ s }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px', marginTop: 14 }}>
    {/* Surface header + headline funnel */}
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{surfaceLabel(s.surface)}</h2>
      <span style={{ fontSize: 12, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
        {fmt(s.reached)} reached · {pct(s.engaged_rate)} engaged · {pct(s.lead_rate)} lead
      </span>
    </div>

    <div style={{ display: 'flex', gap: 0, marginTop: 12, marginBottom: 4 }}>
      <FunnelStage label="Reached" value={s.reached} color={C.accent} />
      <FunnelStage label="Engaged" value={s.engaged} color="#8957e5" />
      <FunnelStage label="Email leads" value={s.leads} color={C.green} />
      {s.would_pay_yes > 0 && <FunnelStage label="Would-pay yes" value={s.would_pay_yes} color={C.green} />}
    </div>

    {/* Per-campaign table */}
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, padding: '0 0 6px' }}>
        <span style={{ flex: 1 }}>Source · campaign</span>
        <span style={{ width: 70, textAlign: 'right' }}>Reached</span>
        <span style={{ width: 70, textAlign: 'right' }}>Engaged</span>
        <span style={{ width: 70, textAlign: 'right' }}>Leads</span>
        <span style={{ width: 56, textAlign: 'right' }}>Lead %</span>
      </div>
      {s.by_campaign.map((c) => (
        <div key={`${c.source}|${c.campaign}`} style={{ display: 'flex', alignItems: 'center', padding: '7px 0', borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
          <span style={{ flex: 1, color: C.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ color: C.text }}>{c.source}</span>
            <span style={{ color: C.muted }}> · {c.campaign}</span>
          </span>
          <span style={{ width: 70, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt(c.reached)}</span>
          <span style={{ width: 70, textAlign: 'right', color: C.dim, fontVariantNumeric: 'tabular-nums' }}>{fmt(c.engaged)}</span>
          <span style={{ width: 70, textAlign: 'right', color: C.green, fontVariantNumeric: 'tabular-nums' }}>{fmt(c.leads)}</span>
          <span style={{ width: 56, textAlign: 'right', color: C.dim, fontVariantNumeric: 'tabular-nums' }}>{pct(c.lead_rate)}</span>
        </div>
      ))}
    </div>
  </div>
);

const FunnelStage: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div style={{ flex: 1, borderLeft: `3px solid ${color}`, paddingLeft: 12, marginRight: 12 }}>
    <p style={{ margin: 0, fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(value)}</p>
    <p style={{ margin: '2px 0 0', fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
  </div>
);

const Stat: React.FC<{ label: string; value: string; sub?: string; valueColor?: string }> = ({ label, value, sub, valueColor }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
    <p style={{ margin: '0 0 4px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted }}>{label}</p>
    <p style={{ margin: 0, fontSize: 26, fontWeight: 700, color: valueColor || C.text, fontVariantNumeric: 'tabular-nums' }}>{value}</p>
    {sub && <p style={{ margin: '2px 0 0', fontSize: 11, color: C.muted }}>{sub}</p>}
  </div>
);

export default DistributionPipeline;
